import React, { useState } from 'react';
import axios from 'axios';
import { Calculator as CalcIcon, DollarSign, Ship, FileText, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Calculator = () => {
  const [vehiclePrice, setVehiclePrice] = useState('');
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async (e) => {
    e.preventDefault();
    
    if (!vehiclePrice || parseFloat(vehiclePrice) <= 0) {
      alert('Please enter a valid vehicle price');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculator/import-cost`, {
        vehicle_price: parseFloat(vehiclePrice)
      });
      setCalculation(response.data);
    } catch (error) {
      console.error('Calculation error:', error);
      alert('Failed to calculate costs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <CalcIcon className="w-8 h-8" />
          </div>
          <h1 className="text-4xl font-heading font-bold mb-4" data-testid="calculator-title">Nigeria Import Calculator</h1>
          <p className="text-textSecondary">
            Estimate the total cost of importing your vehicle to Lagos, Nigeria
          </p>
        </div>

        {/* Calculator Form */}
        <div className="glass p-8 rounded-sm mb-8">
          <form onSubmit={handleCalculate}>
            <label className="block text-lg font-medium mb-4">Vehicle Purchase Price (USD)</label>
            <div className="relative mb-6">
              <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-textSecondary" />
              <input
                type="number"
                value={vehiclePrice}
                onChange={(e) => setVehiclePrice(e.target.value)}
                placeholder="Enter vehicle price"
                data-testid="vehicle-price-input"
                className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-14 pr-4 py-4 text-2xl focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-white/30"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="calculate-button"
              className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase py-4 transition-all duration-300 red-glow disabled:opacity-50"
            >
              {loading ? 'Calculating...' : 'Calculate Total Cost'}
            </button>
          </form>
        </div>

        {/* Results */}
        {calculation && (
          <div className="space-y-4 animate-fadeIn">
            <div className="glass p-6 rounded-sm flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="text-sm text-textSecondary">Vehicle Price</div>
                  <div className="text-2xl font-heading font-bold">${calculation.vehicle_price.toLocaleString()}</div>
                </div>
              </div>
            </div>

            <div className="glass p-6 rounded-sm flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
                  <Ship className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="text-sm text-textSecondary">Shipping to Lagos</div>
                  <div className="text-2xl font-heading font-bold">${calculation.shipping_cost.toLocaleString()}</div>
                </div>
              </div>
            </div>

            <div className="glass p-6 rounded-sm flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="text-sm text-textSecondary">Nigeria Customs Duty (35%)</div>
                  <div className="text-2xl font-heading font-bold">${calculation.customs_duty.toLocaleString()}</div>
                </div>
              </div>
            </div>

            <div className="glass p-6 rounded-sm flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
                  <Truck className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="text-sm text-textSecondary">Clearing & Port Fees (10%)</div>
                  <div className="text-2xl font-heading font-bold">${calculation.clearing_charges.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* Total */}
            <div className="bg-primary p-8 rounded-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm uppercase tracking-wider mb-2">Total Landed Cost in Nigeria</div>
                  <div className="text-5xl font-heading font-black" data-testid="total-cost">
                    ${calculation.total_cost.toLocaleString()}
                  </div>
                </div>
                <CalcIcon className="w-16 h-16 opacity-50" />
              </div>
            </div>

            {/* Info */}
            <div className="glass p-6 rounded-sm">
              <h3 className="font-heading font-bold text-lg mb-3">Important Notes</h3>
              <ul className="space-y-2 text-sm text-textSecondary">
                <li>• These are estimated costs and may vary based on specific vehicle details</li>
                <li>• Customs duty rates are based on current Nigeria regulations (35% of vehicle value)</li>
                <li>• Additional fees may apply for specific vehicle types or conditions</li>
                <li>• Contact us for exact quotes and personalized shipping arrangements</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Calculator;